import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { variableApi } from "../services/api";
import {
  Variable,
  VariableCreate,
  VariableFilters,
  VariableUpdate,
} from "../types/variable";

export const useVariables = (filters: VariableFilters = {}) => {
  return useQuery(
    ["variables", filters],
    () => variableApi.getVariables(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useVariable = (id: number) => {
  return useQuery(["variable", id], () => variableApi.getVariable(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateVariable = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: VariableCreate) => variableApi.createVariable(data),
    {
      onSuccess: (created: Variable) => {
        queryClient.invalidateQueries(["variables"]);
        toast.success(`Variable "${created.var_name}" created successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to create variable";
        toast.error(message);
      },
    },
  );
};

export const useUpdateVariable = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: VariableUpdate }) =>
      variableApi.updateVariable(id, data),
    {
      onSuccess: (updated: Variable) => {
        queryClient.setQueryData(["variable", updated.id], updated);
        queryClient.invalidateQueries(["variables"]);
        toast.success(`Variable "${updated.var_name}" updated successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update variable";
        toast.error(message);
      },
    },
  );
};

export const useDeleteVariable = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => variableApi.deleteVariable(id), {
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries(["variable", deletedId]);
      queryClient.invalidateQueries(["variables"]);
      toast.success("Variable deleted successfully");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete variable";
      toast.error(message);
    },
  });
};
